import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getActiveTrivias,
  getRecentWinners,
  getLatestCompletedTrivias,
  triviaTypeLabels,
  triviaTypeIcons,
  triviaTypeColors,
  triviaModeLabels,
  triviaModeIcons,
  triviaDashboardStats,
  triviaGlobalRanking,
  triviaBadges,
  formatCount,
  getTimeRemainingText,
  type Trivia,
  type TriviaWinner,
} from '@/mocks/rfwTrivias';

function TriviaCard({ trivia, variant = 'default' }: { trivia: Trivia; variant?: 'default' | 'compact' }) {
  const isLive = trivia.status === 'live';
  const isCompleted = trivia.status === 'completed';
  const timeText = getTimeRemainingText(trivia.endDate);

  if (variant === 'compact') {
    return (
      <Link
        to="/trivias"
        className="flex items-start gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200/60 transition-all duration-200 group"
      >
        <div className={`w-10 h-10 rounded-lg ${trivia.type === 'music' ? 'bg-pink-100' : trivia.type === 'radio' ? 'bg-cyan-100' : trivia.type === 'program' ? 'bg-teal-100' : trivia.type === 'host' ? 'bg-amber-100' : 'bg-violet-100'} flex items-center justify-center shrink-0`}>
          <i className={`${triviaTypeIcons[trivia.type]} text-sm ${trivia.type === 'music' ? 'text-pink-600' : trivia.type === 'radio' ? 'text-cyan-600' : trivia.type === 'program' ? 'text-teal-600' : trivia.type === 'host' ? 'text-amber-600' : 'text-violet-600'}`}></i>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-0.5">
            {isLive && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 text-[9px] font-bold">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                EN VIVO
              </span>
            )}
            {isCompleted && (
              <span className="px-1.5 py-0.5 rounded-full bg-foreground-100 text-foreground-500 text-[9px] font-medium">Completada</span>
            )}
            <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full border ${triviaTypeColors[trivia.type]}`}>
              {triviaTypeLabels[trivia.type]}
            </span>
          </div>
          <p className="text-xs font-semibold text-foreground-900 leading-snug mb-0.5 line-clamp-1">{trivia.title}</p>
          <div className="flex items-center gap-2 text-[10px] text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-radio-line text-[9px]"></i>
              {trivia.entityName}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-group-line text-[9px]"></i>
              {formatCount(trivia.participants)}
            </span>
            {!isCompleted && (
              <span className="flex items-center gap-1 text-amber-600">
                <i className="ri-timer-line text-[9px]"></i>
                {timeText}
              </span>
            )}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to="/trivias"
      className="rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-300/60 transition-all duration-300 group"
    >
      {/* Header with gradient */}
      <div className={`h-2 bg-gradient-to-r ${trivia.type === 'music' ? 'from-pink-400 to-rose-400' : trivia.type === 'radio' ? 'from-cyan-400 to-blue-400' : trivia.type === 'program' ? 'from-teal-400 to-emerald-400' : trivia.type === 'host' ? 'from-amber-400 to-orange-400' : 'from-violet-400 to-purple-400'}`}></div>
      <div className="p-4">
        {/* Badges */}
        <div className="flex items-center gap-1.5 mb-2">
          {isLive && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-600 text-[9px] font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
              EN VIVO
            </span>
          )}
          <span className={`px-2 py-0.5 rounded-full text-[9px] font-medium border ${triviaTypeColors[trivia.type]}`}>
            {triviaTypeLabels[trivia.type]}
          </span>
          <span className="px-2 py-0.5 rounded-full text-[9px] font-medium bg-background-100 text-foreground-600">
            {triviaModeLabels[trivia.mode]}
          </span>
        </div>

        {/* Title */}
        <p className="text-sm font-bold text-foreground-900 leading-snug mb-2 line-clamp-2">{trivia.title}</p>
        <p className="text-[11px] text-foreground-500 leading-snug mb-3 line-clamp-2">{trivia.description}</p>

        {/* Meta */}
        <div className="flex items-center gap-2 text-[10px] mb-3">
          <span className="flex items-center gap-1 text-foreground-500">
            <i className="ri-radio-line text-[9px]"></i>
            {trivia.entityName}
          </span>
          {trivia.sponsorName && (
            <span className="flex items-center gap-1 text-violet-600 font-medium">
              <i className="ri-vip-diamond-line text-[9px]"></i>
              {trivia.sponsorName}
            </span>
          )}
        </div>

        {/* Stats row */}
        <div className="flex items-center justify-between text-[11px]">
          <div className="flex items-center gap-3 text-foreground-500">
            <span className="flex items-center gap-1"><i className="ri-questionnaire-line text-[9px]"></i>{trivia.totalQuestions} preguntas</span>
            <span className="flex items-center gap-1"><i className="ri-group-line text-[9px]"></i>{formatCount(trivia.participants)}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-primary-600 font-semibold flex items-center gap-0.5">
              <i className="ri-flashlight-line text-[9px]"></i>+{trivia.score.win} pts
            </span>
            {!isCompleted && (
              <span className={`text-[10px] font-medium ${timeText.includes('min') ? 'text-amber-600' : 'text-foreground-400'}`}>
                <i className="ri-timer-line mr-0.5"></i>{timeText}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

function WinnerCard({ winner }: { winner: TriviaWinner }) {
  const medalEmoji = winner.position === 1 ? '🥇' : winner.position === 2 ? '🥈' : '🥉';
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-amber-200/60 transition-all duration-200">
      <div className="relative shrink-0">
        <img src={winner.avatarUrl} alt={winner.name} className="w-10 h-10 rounded-full object-cover" />
        <span className="absolute -top-1 -right-1 text-sm">{medalEmoji}</span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-foreground-900">{winner.name}</p>
        <div className="flex items-center gap-2 text-[10px] text-foreground-500">
          <span>{winner.correctAnswers}/{winner.totalQuestions} aciertos</span>
          <span>{winner.score} pts</span>
          <span className="text-foreground-300">{winner.time}</span>
        </div>
      </div>
    </div>
  );
}

export default function TriviasSection() {
  const [activeTab, setActiveTab] = useState<'activas' | 'ganadores' | 'ranking'>('activas');

  const activeTrivias = getActiveTrivias().slice(0, 6);
  const recentWinners = getRecentWinners().slice(0, 6);
  const latestCompleted = getLatestCompletedTrivias().slice(0, 4);

  const tabs = [
    { id: 'activas' as const, label: 'Trivias Activas', icon: 'ri-questionnaire-line', count: activeTrivias.length },
    { id: 'ganadores' as const, label: 'Últimos Ganadores', icon: 'ri-trophy-line', count: recentWinners.length },
    { id: 'ranking' as const, label: 'Ranking Trivia', icon: 'ri-medal-line', count: triviaGlobalRanking.length },
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse"></span>
            SISTEMA DE TRIVIAS RFW
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Demostrá lo que Sabés y <span className="text-primary-600">Ganá en Grande</span>
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Trivias de Música, Radio, Programas, Locutores y Patrocinadas. Competí, respondé, sumá Score y ganá premios reales.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'Trivias Activas', value: triviaDashboardStats.todayLive.toString(), icon: 'ri-questionnaire-line', color: 'text-primary-600 bg-primary-50' },
            { label: 'Participantes', value: formatCount(triviaDashboardStats.totalParticipants), icon: 'ri-group-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Respuestas', value: formatCount(triviaDashboardStats.totalQuestionsAnswered), icon: 'ri-chat-check-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Precisión Promedio', value: `${triviaDashboardStats.avgAccuracy}%`, icon: 'ri-focus-3-line', color: 'text-sky-600 bg-sky-50' },
            { label: 'Premios Otorgados', value: triviaDashboardStats.prizesAwarded.toString(), icon: 'ri-gift-line', color: 'text-violet-600 bg-violet-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex bg-background-50 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-primary-500 text-white shadow-sm cursor-pointer'
                    : 'text-foreground-600 hover:text-foreground-900 cursor-pointer'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-background-100 text-foreground-500'
                }`}>{tab.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab: Activas */}
        {activeTab === 'activas' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeTrivias.map((trivia) => (
                <TriviaCard key={trivia.id} trivia={trivia} />
              ))}
            </div>
            {/* Recently completed */}
            {latestCompleted.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-full bg-foreground-100 flex items-center justify-center">
                    <i className="ri-history-line text-sm text-foreground-600"></i>
                  </div>
                  <p className="text-sm font-bold text-foreground-900">Trivias Recientes</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {latestCompleted.map((trivia) => (
                    <TriviaCard key={trivia.id} trivia={trivia} variant="compact" />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab: Ganadores */}
        {activeTab === 'ganadores' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentWinners.map((winner, i) => (
                <WinnerCard key={i} winner={winner} />
              ))}
            </div>
            {/* Badges */}
            <div className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
              <p className="text-sm font-bold text-foreground-900 mb-3 flex items-center gap-2">
                <i className="ri-shield-star-line text-amber-600"></i>
                Insignias de Trivias
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {triviaBadges.map((badge) => (
                  <div key={badge.id} className={`p-3 rounded-xl border ${badge.color}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-xl">{badge.emoji}</span>
                      <p className="text-xs font-bold text-foreground-900">{badge.name}</p>
                    </div>
                    <p className="text-[10px] text-foreground-500 leading-snug">{badge.description}</p>
                    <p className="text-[9px] font-semibold text-primary-600 mt-1.5">+{badge.scoreBonus} pts bonus</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tab: Ranking */}
        {activeTab === 'ranking' && (
          <div>
            <div className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
              {triviaGlobalRanking.slice(0, 8).map((entry, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-3 p-3 ${
                    i < 3
                      ? entry.position === 1
                        ? 'bg-amber-50/50'
                        : entry.position === 2
                        ? 'bg-foreground-100/50'
                        : 'bg-amber-50/30'
                      : 'hover:bg-background-100'
                  } ${i < triviaGlobalRanking.slice(0, 8).length - 1 ? 'border-b border-background-200/70' : ''} transition-colors`}
                >
                  <span className="w-8 text-center text-sm font-extrabold text-foreground-600">
                    {entry.position === 1 ? '🥇' : entry.position === 2 ? '🥈' : entry.position === 3 ? '🥉' : `#${entry.position}`}
                  </span>
                  <img src={entry.avatarUrl} alt={entry.name} className="w-9 h-9 rounded-full object-cover shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-foreground-900">{entry.name}</p>
                    <div className="flex items-center gap-2 text-[10px] text-foreground-500">
                      <span>{entry.totalScore} pts totales</span>
                      <span>·</span>
                      <span>{entry.triviasWon} ganadas</span>
                      <span>·</span>
                      <span>{entry.accuracy}% precisión</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs font-bold text-primary-600">{entry.totalParticipated} trivias</p>
                    <p className="text-[10px] text-foreground-400">{entry.avgTime} avg</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-10">
          <Link
            to="/trivias"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-questionnaire-line"></i>
            Jugar Ahora
            <i className="ri-arrow-right-line"></i>
          </Link>
          <p className="text-xs text-foreground-500 mt-3">
            Competí en trivias de música, radio, programas, locutores y más
          </p>
        </div>

        {/* Regla Final */}
        <div className="mt-10 p-6 rounded-2xl bg-primary-100/70 border border-primary-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-questionnaire-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Las trivias aumentan la permanencia y participación. Cada respuesta correcta es un oyente más comprometido.
          </p>
          <p className="text-xs text-foreground-600">
            No solo se trata de saber — se trata de competir, aprender y conectar con tu comunidad RFW.
          </p>
        </div>
      </div>
    </section>
  );
}